<template>
  <main class="payment-page">
    <div class="breadcrumb">
      <router-link :to="{ name: 'photograph' }">影像服务</router-link
      ><span>/</span><b>付款</b>
    </div>

    <section class="topline">
      <div class="title">
        <h1>付款</h1>
        <p>确认服务与金额后完成支付，可手动刷新并以服务端支付事实确认结果。</p>
      </div>
      <ol class="steps" aria-label="预约进度">
        <li class="done"><i class="el-icon-check"></i><span>选择服务</span></li>
        <li class="done"><i class="el-icon-check"></i><span>确认预约</span></li>
        <li
          :class="{
            current: !presentation.terminal,
            done: presentation.key === 'paid',
          }"
        >
          <i>3</i><span>完成支付</span>
        </li>
        <li :class="{ current: presentation.key === 'paid' }">
          <i>4</i><span>等待服务</span>
        </li>
      </ol>
    </section>

    <section
      v-if="isInitialLoading"
      class="state-card"
      role="status"
      aria-live="polite"
    >
      <i class="el-icon-loading"></i>
      <h2>正在读取预约支付状态</h2>
      <p>请稍候，页面将以服务端记录为准。</p>
    </section>
    <section v-else-if="!appointment" class="state-card error" role="alert">
      <i class="el-icon-warning-outline"></i>
      <h2>支付状态加载失败</h2>
      <p>{{ payment.error || "暂时无法读取预约，请稍后重试。" }}</p>
      <el-button type="primary" @click="refreshPayment">重新加载</el-button>
    </section>

    <div v-else class="layout">
      <section class="stack">
        <article class="card contact-card">
          <div class="card-head">
            <h2>预约联系人</h2>
            <span>用于服务前必要联系</span>
          </div>
          <div class="contact-note">
            <i class="el-icon-lock"></i>
            <div>
              <b>预约联系号码</b
              ><strong class="contact-phone">{{
                appointment.contactPhone || "待确认"
              }}</strong>
              <p>这是本次预约已验证的联系号码，仅向预约本人展示。</p>
            </div>
          </div>
        </article>

        <article class="card booking-card">
          <div class="card-head">
            <h2>预约内容</h2>
          </div>
          <div class="booking-body">
            <div class="booking-type">摄影预约</div>
            <h3>{{ appointment.themeName || "摄影服务" }}</h3>
            <div class="booking-meta">
              <div>
                <span>风格</span
                ><b>{{ appointment.styleName || "以服务端记录为准" }}</b>
              </div>
              <div>
                <span>套餐</span
                ><b>{{ appointment.packageName || "以服务端记录为准" }}</b>
              </div>
              <div>
                <span>化妆服务</span
                ><b>{{ appointment.makeupSelected ? "需要" : "不需要" }}</b>
              </div>
            </div>

            <div class="booking-overview">
              <div class="overview-card">
                <span>拍摄日期</span><b>{{ shootDate }}</b
                ><small>时间均为北京时间</small>
              </div>
              <div class="overview-card">
                <span>拍摄时间</span><b>{{ shootTime }}</b
                ><small>请按预约时间提前准备</small>
              </div>
              <div class="overview-card highlight">
                <span>建议到场</span><b>{{ expectedArrival }}</b
                ><small>{{
                  appointment.makeupSelected
                    ? "含化妆服务，建议提前 1 小时到场"
                    : "摄影服务建议提前 15 分钟到场"
                }}</small>
              </div>
            </div>

            <h4>预约详情</h4>
            <dl class="booking-list">
              <div>
                <dt>拍摄场地</dt>
                <dd>
                  <b>{{ venueFullName }}</b
                  ><small v-if="venueFullAddress">{{ venueFullAddress }}</small>
                </dd>
              </div>
              <div>
                <dt>拍摄人数</dt>
                <dd>
                  <b>{{ appointment.peopleCount || 1 }} 人</b
                  ><small>人数与费用均以创建时快照为准</small>
                </dd>
              </div>
              <div>
                <dt>预约状态</dt>
                <dd>
                  <b>{{ presentation.title }}</b
                  ><small>{{ presentation.description }}</small>
                </dd>
              </div>
            </dl>
            <h4 class="service-detail-title">服务详情</h4>
            <div class="creator-service-grid">
              <section class="creator-service photographer-service">
                <span>摄影师</span>
                <article v-if="photographerDetail" class="creator-profile">
                  <img
                    v-if="
                      photographerDetail.portrait &&
                      photographerDetail.portrait.accessUrl
                    "
                    :src="photographerDetail.portrait.accessUrl"
                    alt="摄影师真人形象照"
                  />
                  <div>
                    <small>摄影师</small
                    ><b>{{
                      photographerDetail.realNameMasked || "实名待确认"
                    }}</b
                    ><em>{{ photographerDetail.professionalIdentityCode }}</em>
                  </div>
                </article>
                <b v-else-if="!appointment.photographerIdentityCode"
                  >公开接单</b
                >
                <b v-else>资料待确认</b>
              </section>
              <section class="creator-service makeup-service">
                <span>化妆师</span>
                <article v-if="makeupArtistDetail" class="creator-profile">
                  <img
                    v-if="
                      makeupArtistDetail.portrait &&
                      makeupArtistDetail.portrait.accessUrl
                    "
                    :src="makeupArtistDetail.portrait.accessUrl"
                    alt="化妆师真人形象照"
                  />
                  <div>
                    <small>化妆师</small
                    ><b>{{
                      makeupArtistDetail.realNameMasked || "实名待确认"
                    }}</b
                    ><em>{{ makeupArtistDetail.professionalIdentityCode }}</em>
                  </div>
                </article>
                <b v-else-if="!appointment.makeupSelected">不需要</b>
                <b v-else-if="!appointment.makeupArtistIdentityCode"
                  >公开接单</b
                >
                <b v-else>资料待确认</b>
              </section>
            </div>
          </div>
        </article>

        <article
          :class="['card', 'result-card', `result-${presentation.key}`]"
          role="status"
          aria-live="polite"
        >
          <i :class="resultIcon"></i>
          <div>
            <h2>{{ presentation.title }}</h2>
            <p>{{ presentation.description }}</p>
            <p v-if="lastUpdatedAt" class="updated">
              最近刷新：{{ lastUpdatedAt }}
            </p>
          </div>
          <el-button
            v-if="presentation.key === 'deadline_reached'"
            plain
            @click="refreshPayment"
            >刷新最终状态</el-button
          >
        </article>
      </section>

      <aside class="card side-card">
        <div class="order-id">
          <span>预约编号</span><strong>{{ appointment.appointmentNo }}</strong>
        </div>
        <div class="summary">
          <h2>
            等待付款<span class="payment-orbit"
              ><img src="@/assets/payment-checkout/f0fcd.svg" alt="" /><img
                class="orbit-dot"
                src="@/assets/payment-checkout/4fce6.svg"
                alt=""
            /></span>
          </h2>
          <div class="price-row">
            <span>优惠前总额</span><b>¥{{ money(appointment.totalAmount) }}</b>
          </div>
          <div class="price-row discount">
            <span>优惠券抵扣</span><b>{{ money(appointment.couponDiscountAmount) === '0.00' ? '— 暂无' : '-¥' + money(appointment.couponDiscountAmount) }}</b>
          </div>
          <div class="price-row total">
            <span>应付金额</span>
            <div>
              <small>¥</small
              ><strong>{{ money(appointment.payableAmount) }}</strong>
            </div>
          </div>
        </div>

        <section class="coupon-summary" aria-label="已锁定优惠券">
          <p><b>优惠券</b> {{ coupons && coupons.selectedCoupon ? coupons.selectedCoupon.templateName : '未使用优惠券' }}</p>
          <small>预约创建后优惠券和金额已锁定；如需修改，请取消预约后重新创建。</small>
          <details v-if="coupons && coupons.selectedCoupon"><summary>使用规则</summary><coupon-rules :coupon="coupons.selectedCoupon" /></details>
          <p v-if="couponError" role="alert">{{ couponError }}</p>
          <el-button v-if="couponError" @click="loadCoupons">重新读取优惠</el-button>
        </section>
        <div class="payment-methods">
          <div class="payment-title">
            <b>支付方式</b><span>以服务端当前支付请求为准</span>
          </div>
          <div class="method-options" role="group" aria-label="选择支付方式">
            <button
              v-for="method in paymentMethods"
              :key="method.method"
              type="button"
              class="method-option"
              :class="{
                selected:
                  selectedChannel === method.channel && method.available,
              }"
              :disabled="
                !method.available ||
                paying ||
                verifying ||
                !canCheckout
              "
              :aria-pressed="
                selectedChannel === method.channel && method.available
              "
              @click="
                selectedChannel = method.channel;
                paymentIntentKey = '';
                forgetPaymentIntent();
              "
            >
              <img
                class="method-logo"
                v-if="method.method !== 1"
                :src="methodIcon(method.method)"
                alt=""
              /><img
                class="method-logo wallet-logo"
                v-else
                src="@/assets/payment-checkout/39aa3.svg"
                alt=""
              />
              <span
                ><b>{{ method.methodName }}支付</b
                ><small v-if="!method.available">{{
                  method.disabledReason || "当前客户端暂不可用"
                }}</small></span
              ><img
                class="selection-icon"
                :src="
                  selectionIcon(
                    selectedChannel === method.channel && method.available
                  )
                "
                alt=""
              />
            </button>
          </div>
          <p v-if="!paymentMethods.length" class="no-channel">
            {{ paymentMethodsError || "支付方式暂不可用，请重新加载。" }}
          </p>
          <el-button v-if="!paymentMethods.length" plain @click="reloadPaymentMethods">重新加载支付方式</el-button>
          <div
            v-if="nativeCodeUrl"
            class="native-payment"
            role="group"
            aria-label="微信扫码支付二维码"
          >
            <vue-qr
              :text="nativeCodeUrl"
              :size="208"
              :margin="8"
              :auto-color="true"
              :dot-scale="1"
            />
            <b>请使用微信扫码支付</b>
            <small>二维码仅在本机使用随包组件生成；扫码不代表支付成功。</small>
          </div>
        </div>

        <div class="checkout">
          <p v-if="showDeadline" class="deadline">
            请在
            <b>{{ countdown }}</b>
            内完成支付。付款后请点击“已完成支付”核实结果。
          </p>
          <div
            v-if="appointment.appointmentStatus === 1"
            class="checkout-actions"
          >
            <el-button
              type="warning"
              :loading="paying"
              :disabled="!canCheckout || cancellationLoading || verifying"
              @click="payNow"
              >支付</el-button
            >
            <el-button
              :loading="cancellationLoading"
              :disabled="paying || verifying || refreshing"
              @click="cancelAppointment"
              >取消预约</el-button
            >
          </div>
          <el-button
            v-if="presentation.shouldPoll"
            plain
            :disabled="verifying"
            @click="refreshPayment"
            >刷新支付结果</el-button
          >
          <el-button
            v-if="
              switchOutcomeUnknown && paymentIntentKey
            "
            class="verify-button"
            :loading="paying || paymentIntentRecovering"
            @click="retryOriginalPayment"
            >重试原支付请求</el-button
          >
          <el-button
            v-if="showVerifyButton"
            class="verify-button"
            :loading="verifying"
            :disabled="
              refreshing || cancellationLoading || switchingPayment || paying
            "
            @click="verifyPayment"
            >已完成支付</el-button
          >
          <p
            v-if="verificationMessage"
            class="verification-message"
            role="status"
          >
            {{ verificationMessage }}
          </p>
          <p v-if="verificationError" class="cancellation-error" role="alert">
            {{ verificationError }}
          </p>
          <p v-if="cancellationError" class="cancellation-error" role="alert">
            {{ cancellationError }}
          </p>
          <div class="agreement">
            支付返回页不代表支付成功，最终结果只以本页恢复的服务端记录为准。
          </div>
        </div>
        <div class="why">
          <strong>金额说明：</strong
          >页面只展示服务端创建预约时固化的费用快照，不在客户端重新计算应付金额。
        </div>
      </aside>
    </div>

  </main>
</template>

<script>
import { mapActions } from "vuex";
import VueQr from "vue-qr";
import request from "@/tool/request";
import API from "@/tool/api";
import CouponRules from "@/components/coupons/CouponRules.vue";
import { unwrapResult } from "@/tool/result";
import photographyPayment from "@/tool/photographyPayment.cjs";
const { APPOINTMENT, PAYMENT, REFUND, SERVICE, ASSIGNMENT } =
  photographyPayment.STATUS_CODES;
import photographyBooking from "@/tool/photographyBooking.cjs";
import { getResultMessage } from "@/tool/result";

const { parseWechatNativeCodeUrl, resolvePhotographyPaymentState } =
  photographyPayment;
const { isDirectedCreatorPortraitFresh } = photographyBooking;

export default {
  name: "PhotographyAppointmentPayment",
  components: { VueQr, CouponRules },
  data() {
    return {
      payment: { status: "idle", appointment: null, error: "" },
      paymentChannels: [],
      paymentMethodsError: "",
      coupons: null,
      couponsLoading: false,
      couponError: "",
      paymentReconciliationRequired: false,
      couponReadGeneration: 0,
      paymentIntentKey: "",
      paymentIntentDetails: null,
      paymentHistory: [],
      paymentIntentRecovering: false,
      selectedChannel: "",
      requestGeneration: 0,
      disposed: false,
      countdownTimer: null,
      remainingSeconds: 0,
      refreshing: false,
      paying: false,
      cancellationLoading: false,
      cancellationError: "",
      switchingPayment: false,
      verifying: false,
      verificationMessage: "",
      verificationError: "",
      switchOutcomeUnknown: false,
      deadlineRefreshRequested: false,
      photographerPreview: null,
      makeupArtistPreview: null,
      venueDetail: null,
      lastUpdatedAt: "",
      redirectingToAppointment: false,
    };
  },
  computed: {
    identity() {
      const user = this.$store.state.user;
      return user && user.is_login && user.user_id
        ? `${user.user_id}:${user.sessionVersion}`
        : "";
    },
    contextKey() {
      return `${this.identity}|${this.appointmentNo}`;
    },
    canCheckout() {
      return (
        this.appointment &&
        this.appointment.appointmentStatus === APPOINTMENT.WAITING_PAYMENT &&
        this.remainingSeconds > 0 &&
        !this.switchOutcomeUnknown &&
        !this.paymentReconciliationRequired &&
        !this.paymentIntentRecovering &&
        !this.refreshing &&
        !this.couponsLoading &&
        !(
          this.activePaymentRequest &&
          [PAYMENT.CREATING, PAYMENT.CREATE_UNKNOWN].includes(
            this.activePaymentRequest.paymentStatus
          )
        )
      );
    },
    appointmentNo() {
      return this.$route.params.appointmentNo || "";
    },
    appointment() {
      return this.payment.appointment;
    },
    isInitialLoading() {
      return this.payment.status === "loading" && !this.appointment;
    },
    presentation() {
      const appointment = this.appointment
        ? {
            ...this.appointment,
            paymentRemainingSeconds: this.remainingSeconds,
          }
        : {};
      if (this.switchOutcomeUnknown && appointment.currentPaymentRequest) {
        appointment.currentPaymentRequest = {
          ...appointment.currentPaymentRequest,
          paymentStatus: PAYMENT.CREATE_UNKNOWN,
          invocationPayload: "",
        };
      }
      return resolvePhotographyPaymentState(appointment);
    },
    paymentMethods() {
      return this.paymentChannels;
    },
    nativeCodeUrl() {
      return !this.paying && this.presentation.key === "pending" && this.paymentMethods.some(method => method.available && this.activePaymentRequest && method.channel === this.activePaymentRequest.channel)
        ? parseWechatNativeCodeUrl(this.activePaymentRequest || {})
        : "";
    },
    activePaymentRequest() {
      if (!this.appointment) return null;
      return this.appointment.currentPaymentRequest || null;
    },
    showVerifyButton() {
      return (
        this.activePaymentRequest &&
        [PAYMENT.PENDING, PAYMENT.CREATE_UNKNOWN, PAYMENT.CREATING].includes(
          this.activePaymentRequest.paymentStatus
        )
      );
    },
    photographerDetail() {
      return (
        this.photographerPreview ||
        (this.appointment && this.appointment.photographer) ||
        null
      );
    },
    makeupArtistDetail() {
      return (
        this.makeupArtistPreview ||
        (this.appointment && this.appointment.makeupArtist) ||
        null
      );
    },
    countdown() {
      const minutes = Math.floor(this.remainingSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (this.remainingSeconds % 60).toString().padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    showDeadline() {
      return (
        this.appointment &&
        this.appointment.appointmentStatus === APPOINTMENT.WAITING_PAYMENT &&
        this.remainingSeconds > 0
      );
    },
    venueFullName() {
      if (!this.appointment) return "以服务端记录为准";
      return (
        [
          (this.venueDetail && this.venueDetail.venueName) ||
            this.appointment.venueName,
          (this.venueDetail && this.venueDetail.pointName) ||
            this.appointment.pointName,
        ]
          .filter(Boolean)
          .join(" · ") || "以服务端记录为准"
      );
    },
    venueFullAddress() {
      if (this.venueDetail && this.venueDetail.fullAddress)
        return this.venueDetail.fullAddress;
      if (!this.appointment) return "";
      return [
        this.appointment.provinceName,
        this.appointment.cityName,
        this.appointment.districtName,
        this.appointment.venueLocationSummary,
        this.appointment.venueName,
        this.appointment.pointName,
      ]
        .filter(
          (value, index, values) => value && values.indexOf(value) === index
        )
        .join(" / ");
    },
    shootDate() {
      return (this.appointment.shootAt || "").slice(0, 10) || "待确认";
    },
    shootTime() {
      return (this.appointment.shootAt || "").slice(11, 16) || "待确认";
    },
    expectedArrival() {
      const value = this.appointment && this.appointment.shootAt;
      if (!value) return "待确认";
      const match = / (\d{2}):(\d{2}):\d{2}$/.exec(value);
      if (!match) return "待确认";
      const leadMinutes = this.appointment.makeupSelected ? 60 : 15;
      const total = Number(match[1]) * 60 + Number(match[2]) - leadMinutes;
      const normalized = (total + 24 * 60) % (24 * 60);
      return `${Math.floor(normalized / 60)
        .toString()
        .padStart(2, "0")}:${(normalized % 60).toString().padStart(2, "0")}`;
    },
    resultIcon() {
      if (this.presentation.key === "paid") return "el-icon-success";
      if (this.presentation.key === "ready") return "el-icon-time";
      if (
        ["closed", "deadline_reached", "request_unavailable"].includes(
          this.presentation.key
        )
      )
        return "el-icon-warning-outline";
      return "el-icon-loading";
    },
    editRoute() {
      return { name: "photograph" };
    },
    appointmentDetailRoute() {
      return {
        name: "photographyAppointmentDetail",
        params: { appointmentNo: this.appointmentNo },
      };
    },
  },
  watch: {
    contextKey() {
      this.clearPhotographyPayment();
      this.couponReadGeneration++;
      this.coupons = null;
      this.paymentReconciliationRequired = false;
      this.paymentIntentKey = "";
      this.paymentIntentDetails = null;
      this.paymentHistory = [];
      this.paymentIntentRecovering = false;
      this.selectedChannel = "";
      this.paymentChannels = [];
      this.paymentMethodsError = "";
      this.switchOutcomeUnknown = false;
      this.photographerPreview = null;
      this.makeupArtistPreview = null;
      this.venueDetail = null;
      this.stopTimers();
      this.refreshing = false;
      this.paying = false;
      this.verifying = false;
      this.cancellationLoading = false;
      this.verificationError = "";
      this.couponError = "";
      this.cancellationError = "";
      if (this.identity) {
        this.restorePaymentIntent();

        this.loadPhotographyPaymentChannels().catch(() => null);
        this.refreshPayment();
      }
    },
  },
  created() {
    this.remainingSeconds = this.appointment
      ? this.appointment.paymentRemainingSeconds
      : 0;
    window.addEventListener("focus", this.handleFocus);
    if (this.identity) {
      this.restorePaymentIntent();

      this.loadPhotographyPaymentChannels().catch(() => null);
      this.refreshPayment();
    }
  },
  beforeDestroy() {
    this.disposed = true;
    window.removeEventListener("focus", this.handleFocus);
    this.stopTimers();
    this.clearPhotographyPayment();
  },
  methods: {
    ...mapActions([
      "verifyPhotographyAppointmentPayment",
      "loadPhotographyPaymentVenue",
      "prevalidatePhotographyProfessionalIdentity",
      "submitPhotographyAppointmentCancellation",
    ]),
    clearPhotographyPayment() {
      this.requestGeneration++;
      this.payment = { status: "idle", appointment: null, error: "" };
    },
    async loadPhotographyAppointmentPayment(appointmentNo) {
      const context = this.contextKey;
      const generation = ++this.requestGeneration;
      this.payment.status = "loading";
      try {
        const raw = unwrapResult(
          await request.get(API.photography.appointmentDetail(appointmentNo), {
            headers: { "Cache-Control": "no-store" },
          }),
          "预约加载失败"
        );
        if (!raw || raw.appointmentNo !== appointmentNo || ![1, 2, 3].includes(raw.appointmentStatus)
          || [raw.totalAmount, raw.payableAmount].some(value => value == null || !Number.isFinite(Number(value)) || Number(value) < 0)) throw new Error("预约金额信息不完整，请重新加载");
        const appointment =
          photographyPayment.normalizePhotographyPaymentAppointment(raw);
        if (
          context !== this.contextKey ||
          generation !== this.requestGeneration ||
          this.disposed ||
          appointmentNo !== this.appointmentNo
        )
          throw new Error("预约已变化");
        this.paymentHistory = (Array.isArray(raw.paymentAttempts) ? raw.paymentAttempts : []).map(photographyPayment.normalizePhotographyPaymentRequest);
        this.payment = { status: "ready", appointment, error: "" };
        this.paymentReconciliationRequired = false;
        return appointment;
      } catch (error) {
        if (
          context === this.contextKey &&
          generation === this.requestGeneration &&
          !this.disposed
        ) {
          this.payment.status = "error";
          this.payment.error = getResultMessage(error, "预约加载失败");
        }
        throw error;
      }
    },
    reloadPaymentMethods() { return this.loadPhotographyPaymentChannels().catch(() => null); },
    async loadPhotographyPaymentChannels() {
      const context = this.contextKey;
      this.paymentMethodsError = "";
      try {
        const payload = unwrapResult(await request.get(API.photography.paymentChannels, { headers: { "Cache-Control": "no-store" } }), "支付方式加载失败");
        if (context !== this.contextKey || this.disposed) return [];
        const allowed = photographyPayment.normalizeAvailablePaymentChannels(payload, photographyPayment.getBrowserPhotographyPaymentChannel(typeof navigator === "undefined" ? {} : navigator));
        this.paymentChannels = (payload.paymentMethods || []).map(item => ({ ...item, available: item.available === true && allowed.some(channel => channel.channel === item.channel) }));
        return this.paymentChannels;
      } catch(error) {
        if (context === this.contextKey && !this.disposed) { this.paymentChannels = []; this.paymentMethodsError = getResultMessage(error, "支付方式加载失败，请重试"); }
        throw error;
      }
    },
    selectionIcon(selected) {
      return selected
        ? require("@/assets/payment-checkout/a72ff.svg")
        : require("@/assets/payment-checkout/9dc8f.svg");
    },
    methodIcon(method) {
      return method === 2
        ? require("@/assets/payment-checkout/a19cf.png")
        : require("@/assets/payment-checkout/49ae8.png");
    },
    async loadCoupons() {
      const context = this.contextKey,
        generation = ++this.couponReadGeneration;
      this.couponsLoading = true;
      this.couponError = "";
      try {
        const data = unwrapResult(
          await request.get(
            API.photography.appointmentCoupons(this.appointmentNo),
            { headers: { "Cache-Control": "no-store" } }
          ),
          "优惠券加载失败"
        );
        if (
          context !== this.contextKey ||
          generation !== this.couponReadGeneration ||
          this.disposed
        )
          return;
        this.validateCoupons(data);
        this.applyCoupons(data, false);
        await this.reconcileStoredPaymentIntent();
      } catch (error) {
        if (
          context === this.contextKey &&
          generation === this.couponReadGeneration &&
          !this.disposed
        )
          this.couponError = getResultMessage(error, "优惠券加载失败");
      } finally {
        if (
          context === this.contextKey &&
          generation === this.couponReadGeneration &&
          !this.disposed
        )
          this.couponsLoading = false;
      }
    },
    applyCoupons(data, changed) {
      const changedPrice =
        this.appointment &&
        (Number(this.appointment.payableAmount) !==
          Number(data.payableAmount) ||
          (this.coupons &&
            this.coupons.selectedCouponId !== data.selectedCouponId));
      if (changedPrice && !changed && !this.switchOutcomeUnknown) {
        this.paymentIntentKey = "";
        this.forgetPaymentIntent();
      }
      changed = changed || changedPrice;
      this.coupons = data;
      if (this.appointment)
        this.payment.appointment = {
          ...this.appointment,
          appointmentStatus: data.appointmentStatus,
          paymentDeadline:
            data.paymentDeadline || this.appointment.paymentDeadline,
          totalAmount: data.preCouponAmount,
          couponDiscountAmount: data.discountAmount,
          payableAmount: data.payableAmount,
          ...(changed ? { currentPaymentRequest: null } : {}),
        };
      if (data.appointmentStatus === APPOINTMENT.PAID) {
          this.stopTimers();
        this.$router.replace(this.appointmentDetailRoute);
      }
    },
    validateCoupons(data) {
      if (!data || data.appointmentNo !== this.appointmentNo || ![1,2,3].includes(data.appointmentStatus)
          || !Array.isArray(data.candidates) || !Array.isArray(data.allocations)
          || [data.preCouponAmount,data.discountAmount,data.payableAmount,data.paidAmount].some(value => value == null || !Number.isFinite(Number(value)) || Number(value) < 0)
          || data.candidates.some(item => !item.coupon || !item.coupon.couponId || !item.preview || typeof item.preview.available !== "boolean")) throw new Error("优惠信息暂时无法读取，请重试");
    },
    intentStorageKey() {
      return `photography-payment:${this.contextKey}`;
    },
    forgetPaymentIntent() {
      this.paymentIntentDetails = null;
      try { window.sessionStorage.removeItem(this.intentStorageKey()); } catch(error) { /* storage may be unavailable */ }
    },
    restorePaymentIntent() {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(this.intentStorageKey()) || "null");
        if (stored && typeof stored.key === "string" && stored.key.length > 0 && stored.key.length <= 100) {
          this.paymentIntentKey = stored.key;
          this.paymentIntentDetails = stored;
          this.selectedChannel = stored.channel || "";
          this.switchOutcomeUnknown = true;
        }
      } catch(error) { /* no previous valid intent */ }
    },
    savePaymentIntent() {
      try { window.sessionStorage.setItem(this.intentStorageKey(), JSON.stringify(this.paymentIntentDetails)); } catch(error) { /* memory intent remains stable */ }
    },
    rememberPaymentIntent(payload, paymentRequest) {
      if (!this.paymentIntentDetails || this.paymentIntentDetails.key !== this.paymentIntentKey) {
        const originalPayload = payload || { idempotencyKey: this.paymentIntentKey, ...(this.selectedChannel ? {channel:this.selectedChannel} : {}) };
        this.paymentIntentDetails = { version:2, key:this.paymentIntentKey, channel:originalPayload.channel || "",
          amount:this.appointment.payableAmount, selectedCouponId:this.coupons ? this.coupons.selectedCouponId : null,
          payload:originalPayload, paymentRequestNo:"", outcome:"unresolved" };
      }
      if (paymentRequest && paymentRequest.paymentRequestNo) {
        this.paymentIntentDetails = { ...this.paymentIntentDetails, paymentRequestNo:paymentRequest.paymentRequestNo,
          amount:paymentRequest.amount, outcome:paymentRequest.paymentStatus === PAYMENT.PENDING ? "accepted" : "unresolved" };
      }
      this.savePaymentIntent();
    },
    retirePaymentIntent() {
      this.paymentIntentKey = "";
      this.forgetPaymentIntent();
      this.switchOutcomeUnknown = false;
      this.selectedChannel = this.activePaymentRequest ? this.activePaymentRequest.channel : "";
    },
    matchesStoredPayment(request, intent) {
      return request && request.paymentRequestNo === intent.paymentRequestNo && request.channel === intent.channel
        && (intent.amount == null || Number(request.amount) === Number(intent.amount));
    },
    isResolvedPayment(request) {
      if (!request) return false;
      if ([PAYMENT.CREATE_FAILED, PAYMENT.CLOSED, PAYMENT.EXPIRED].includes(request.paymentStatus)) return true;
      return [PAYMENT.LATE_SUCCEEDED, PAYMENT.DUPLICATE_SUCCEEDED].includes(request.paymentStatus)
        && request.refundStatus === REFUND.SUCCEEDED && typeof request.refundNo === "string" && request.refundNo.trim().length > 0
        && Number(request.amount) > 0 && Number(request.refundAmount) === Number(request.amount);
    },
    async reconcileStoredPaymentIntent(forceVerify = false) {
      const intent = this.paymentIntentDetails, context = this.contextKey;
      if (!intent || this.paymentIntentRecovering) return;
      const current = this.activePaymentRequest;
      if (!intent.paymentRequestNo && current && intent.version === 2 && intent.amount != null && this.coupons
          && current.channel === intent.channel && Number(current.amount) === Number(intent.amount)
          && intent.selectedCouponId === this.coupons.selectedCouponId) {
        this.rememberPaymentIntent(intent.payload,current);
        return this.reconcileStoredPaymentIntent(forceVerify);
      }
      if (!intent.paymentRequestNo) return;
      const historical = this.paymentHistory.find(item => this.matchesStoredPayment(item,intent));
      if (!forceVerify && this.isResolvedPayment(historical)) { this.retirePaymentIntent(); return; }
      if (!forceVerify && this.matchesStoredPayment(current,intent)) {
        if (this.isResolvedPayment(current)) this.retirePaymentIntent();
        else this.switchOutcomeUnknown = current.paymentStatus !== PAYMENT.PENDING;
        return;
      }
      if (!forceVerify && current) return;
      this.paymentIntentRecovering = true;
      try {
        const verified = await this.verifyPhotographyAppointmentPayment({appointmentNo:this.appointmentNo,paymentRequestNo:intent.paymentRequestNo});
        if (context !== this.contextKey || this.disposed || this.paymentIntentDetails !== intent) return;
        if (this.matchesStoredPayment(verified,intent) && this.isResolvedPayment(verified)) {
          this.retirePaymentIntent();
          // A terminal request proves this intent is over; only a later explicit click may create the current-price payment.
        } else if (this.matchesStoredPayment(verified,intent) && verified.paymentStatus === PAYMENT.PENDING
            && current && current.paymentRequestNo === verified.paymentRequestNo
            && Number(verified.amount) === Number(this.appointment.payableAmount)) {
          this.rememberPaymentIntent(intent.payload,verified); this.switchOutcomeUnknown = false;
        } else if (verified && verified.paymentStatus === PAYMENT.SUCCEEDED) {
          const latest = await this.loadPhotographyAppointmentPayment(this.appointmentNo);
          if (context === this.contextKey && !this.disposed && latest.appointmentStatus === APPOINTMENT.PAID) { this.retirePaymentIntent(); this.$router.replace(this.appointmentDetailRoute); }
        } else this.verificationMessage = "原支付请求仍需核实，请刷新状态后重试。";
      } catch(error) {
        if (context === this.contextKey && !this.disposed) this.verificationError = getResultMessage(error,"原支付请求暂时无法核实，请稍后重试");
      } finally { if (context === this.contextKey && !this.disposed) this.paymentIntentRecovering = false; }
    },
    async retryOriginalPayment() {
      if (!this.switchOutcomeUnknown || !this.paymentIntentKey || this.paymentIntentRecovering) return;
      const intent = this.paymentIntentDetails;
      if (intent && intent.paymentRequestNo) return this.reconcileStoredPaymentIntent(true);
      // Old clients did not save a request number. Only an unambiguous owned history entry may be explicitly verified; never guess among attempts.
      const candidates = this.paymentHistory.filter(item => item.paymentRequestNo && item.paymentStatus != null && item.channel === (intent && intent.channel || this.selectedChannel)
        && (intent && intent.amount != null ? Number(item.amount) === Number(intent.amount) : true));
      if (candidates.length === 1 && (!this.activePaymentRequest || this.activePaymentRequest.paymentRequestNo === candidates[0].paymentRequestNo)) {
        this.paymentIntentDetails = {...intent,key:this.paymentIntentKey,paymentRequestNo:candidates[0].paymentRequestNo,amount:candidates[0].amount};
        this.savePaymentIntent();
        return this.reconcileStoredPaymentIntent(true);
      }
      const sameEconomics = intent && intent.version === 2 && intent.amount != null && this.coupons
        && Number(intent.amount) === Number(this.appointment.payableAmount)
        && intent.selectedCouponId === this.coupons.selectedCouponId;
      if (!sameEconomics) {
        this.verificationError = "原付款目标已变化或旧版信息不完整，请先核实原支付请求；不能把原键用于当前金额。";
        return;
      }
      return this.payNow(true);
    },
    money(value) {
      const amount = Number(value);
      return Number.isFinite(amount) && amount >= 0
        ? amount.toFixed(2)
        : "0.00";
    },
    refreshPayment() {
      if (
        !this.identity ||
        !this.appointmentNo ||
        this.refreshing ||
        this.verifying ||
        this.paying
      )
        return Promise.resolve();
      const context = this.contextKey;
      this.refreshing = true;
      return this.loadPhotographyAppointmentPayment(this.appointmentNo)
        .then((appointment) => {
          if (
            appointment.appointmentStatus === APPOINTMENT.PAID &&
            !this.redirectingToAppointment
          ) {
            this.forgetPaymentIntent();
            this.redirectingToAppointment = true;
            this.stopTimers();
            this.$router.replace(this.appointmentDetailRoute);
            return appointment;
          }
          this.remainingSeconds = appointment.paymentRemainingSeconds;
          if (appointment.currentPaymentRequest)
            this.selectedChannel = appointment.currentPaymentRequest.channel;
          if (appointment.currentPaymentRequest)
            this.switchOutcomeUnknown = !!this.paymentIntentDetails
              || [PAYMENT.CREATE_UNKNOWN, PAYMENT.CREATING].includes(appointment.currentPaymentRequest.paymentStatus);
          if (this.remainingSeconds > 0) this.deadlineRefreshRequested = false;
          this.loadVenueDetails(appointment);
          this.loadCreatorDetails(appointment);
          this.lastUpdatedAt = new Date().toLocaleTimeString("zh-CN", {
            hour12: false,
          });
          this.startCountdown();
          return this.loadCoupons().then(() => appointment);
        })
        .catch(() => null)
        .finally(() => {
          if (context === this.contextKey && !this.disposed)
            this.refreshing = false;
        });
    },
    startCountdown() {
      clearInterval(this.countdownTimer);
      if (this.remainingSeconds <= 0) return;
      this.countdownTimer = setInterval(() => {
        this.remainingSeconds = Math.max(0, this.remainingSeconds - 1);
        if (this.remainingSeconds === 0) {
          clearInterval(this.countdownTimer);
          if (!this.deadlineRefreshRequested) {
            this.deadlineRefreshRequested = true;
            this.refreshPayment();
          }
        }
      }, 1000);
    },
    stopTimers() {
      clearInterval(this.countdownTimer);
    },
    handleFocus() {
      this.refreshPayment();
    },
    loadVenueDetails(appointment) {
      const context = this.contextKey;
      if (!appointment || !appointment.serviceItemId || !appointment.venueId) {
        this.venueDetail = null;
        return Promise.resolve(null);
      }
      return this.loadPhotographyPaymentVenue({
        serviceItemId: appointment.serviceItemId,
        venueId: appointment.venueId,
        pointId: appointment.pointId,
      })
        .then((detail) => {
          if (context !== this.contextKey || this.disposed) return null;
          this.venueDetail = detail;
          return detail;
        })
        .catch(() => {
          if (context !== this.contextKey || this.disposed) return null;
          this.venueDetail = null;
          return null;
        });
    },
    loadCreatorDetails(appointment) {
      const context = this.contextKey;
      const load = (role, code, embedded) => {
        const previewField =
          role === "photographer"
            ? "photographerPreview"
            : "makeupArtistPreview";
        if (!code) {
          this[previewField] = null;
          return;
        }
        if (embedded && isDirectedCreatorPortraitFresh(embedded)) {
          this[previewField] = embedded;
          return;
        }
        this.prevalidatePhotographyProfessionalIdentity({
          professionalIdentityCode: code,
          expectedProfessionalType:
            role === "photographer" ? "PHOTOGRAPHER" : "MAKEUP_ARTIST",
          serviceItemId: appointment.serviceItemId,
          shootAt: appointment.shootAt,
        })
          .then((creator) => {
            const codeField =
              role === "photographer"
                ? "photographerIdentityCode"
                : "makeupArtistIdentityCode";
            if (
              context === this.contextKey &&
              !this.disposed &&
              this.appointment &&
              this.appointment[codeField] === code
            )
              this[previewField] = creator;
          })
          .catch(() => {
            if (context === this.contextKey && !this.disposed)
              this[previewField] = null;
          });
      };
      load(
        "photographer",
        appointment.photographerIdentityCode,
        appointment.photographer
      );
      if (appointment.makeupSelected)
        load(
          "makeupArtist",
          appointment.makeupArtistIdentityCode,
          appointment.makeupArtist
        );
      else this.makeupArtistPreview = null;
    },
    async cancelAppointment() {
      if (
        !this.appointment ||
        this.appointment.appointmentStatus !== APPOINTMENT.WAITING_PAYMENT ||
        this.cancellationLoading ||
        this.verifying ||
        this.paying
      )
        return;
      const context = this.contextKey;
      this.cancellationLoading = true;
      this.cancellationError = "";
      try {
        await this.$confirm(
          "取消后将结束本次预约并释放优惠券。服务端会先核实未完成的支付，是否继续？",
          "取消预约",
          {
            confirmButtonText: "确认取消预约",
            cancelButtonText: "保留预约",
            type: "warning",
          }
        );
        if (context !== this.contextKey || this.disposed) return;
        await this.submitPhotographyAppointmentCancellation({
          appointmentNo: this.appointmentNo,
          cancellationKind: "UNPAID",
          targetResourceType: "APPOINTMENT",
          serviceOrderNos: [],
        });
        if (context !== this.contextKey || this.disposed) return;
        this.forgetPaymentIntent();
        this.stopTimers();
        this.$router.replace(this.appointmentDetailRoute);
      } catch (error) {
        if (
          context === this.contextKey &&
          !this.disposed &&
          error !== "cancel" &&
          error !== "close"
        )
          this.cancellationError = getResultMessage(
            error,
            "取消预约失败，请核实原支付结果后重试"
          );
      } finally {
        if (context === this.contextKey && !this.disposed)
          this.cancellationLoading = false;
      }
    },
    async payNow(restoreOriginal = false) {
      const restoring = restoreOriginal === true;
      if (
        !this.appointment ||
        this.paying ||
        this.verifying ||
        this.cancellationLoading ||
        this.switchingPayment ||
        this.appointment.appointmentStatus !== APPOINTMENT.WAITING_PAYMENT ||
        this.remainingSeconds <= 0
      )
        return;
      if (!this.canCheckout && !(restoring && this.switchOutcomeUnknown && this.paymentIntentKey)) return;
      const context = this.contextKey;
      const original = restoring ? null : this.activePaymentRequest;
      if (
        original &&
        [PAYMENT.CREATING, PAYMENT.CREATE_UNKNOWN].includes(
          original.paymentStatus
        )
      )
        return this.verifyPayment();
      if (
        Number(this.appointment.payableAmount) !== 0 &&
        !this.selectedChannel &&
        !original
      ) {
        this.verificationError = "请选择可用支付方式";
        return;
      }
      const channel = restoring && this.paymentIntentDetails ? this.paymentIntentDetails.channel : this.selectedChannel || (original && original.channel);
      if (Number(this.appointment.payableAmount) !== 0 && !this.paymentMethods.some(method => method.available && method.channel === channel)) {
        this.verificationError = "请选择当前客户端可用的支付方式，原支付结果可继续核实。";
        return;
      }
      this.paying = true;
      this.requestGeneration++;
      this.couponReadGeneration++;
      this.verificationError = "";
      try {
        let current = original;
        if (
          !current ||
          current.paymentStatus !== PAYMENT.PENDING ||
          (this.selectedChannel && current.channel !== this.selectedChannel)
        ) {
          if (!this.paymentIntentKey)
            this.paymentIntentKey = `payment-${Date.now().toString(
              36
            )}-${Math.random().toString(36).slice(2, 14)}`;
          let payload;
          if (this.paymentIntentDetails && this.paymentIntentDetails.key === this.paymentIntentKey) {
            payload = {idempotencyKey:this.paymentIntentKey,...(this.paymentIntentDetails.channel ? {channel:this.paymentIntentDetails.channel} : {})};
          } else {
            payload = {idempotencyKey:this.paymentIntentKey};
            if (Number(this.appointment.payableAmount) !== 0) payload.channel = this.selectedChannel;
          }
          this.rememberPaymentIntent(payload);
          const response = await request.post(
            API.photography.appointmentPaymentRequests(this.appointmentNo),
            payload,
            { headers: { "Content-Type": "application/json" } }
          );
          if (context !== this.contextKey || this.disposed) return;
          current = response && response.data && response.data.data;
          if (
            !current ||
            ![202, 200].includes(Number(response.status || response.data.code))
          )
            throw new Error("支付请求结果待核实，请刷新原预约");
          this.rememberPaymentIntent(payload,current);
          if (this.isResolvedPayment(current)) {
            this.retirePaymentIntent();
            await this.loadPhotographyAppointmentPayment(this.appointmentNo);
            if (context !== this.contextKey || this.disposed) return;
            await this.loadCoupons();
            this.verificationMessage = "原支付意图已结束，请核对当前金额后重新点击支付。";
            return;
          }
          if (current.paymentStatus === PAYMENT.PENDING && Number(current.amount) !== Number(this.appointment.payableAmount)) {
            // Another device may have changed the coupon after this page rendered. Show the new server price before opening a cashier.
            await this.loadPhotographyAppointmentPayment(this.appointmentNo);
            if (context !== this.contextKey || this.disposed) return;
            await this.loadCoupons();
            if (context === this.contextKey && !this.disposed) this.verificationMessage = "预约金额已变化，请核对最新优惠与金额后再次点击支付。";
            return;
          }
          this.payment.appointment =
            photographyPayment.normalizePhotographyPaymentAppointment({
              ...this.appointment,
              appointmentStatus:
                current.paymentStatus === PAYMENT.SUCCEEDED
                  ? APPOINTMENT.PAID
                  : this.appointment.appointmentStatus,
              paymentStatus: current.paymentStatus,
              currentPaymentRequest: current.paymentRequestNo ? current : null,
            });
        }
        if (current.paymentStatus === PAYMENT.SUCCEEDED) {
          this.forgetPaymentIntent();
          this.$router.replace(this.appointmentDetailRoute);
          return;
        }
        if (current.paymentStatus !== PAYMENT.PENDING) {
          this.switchOutcomeUnknown = true;
          this.verificationMessage = "支付入口确认中，请核实原请求";
          return;
        }
        this.switchOutcomeUnknown = false;
        if (
          current.invocationPayload &&
          photographyPayment.isAlipayWebsitePaymentChannel(current.channel)
        ) {
          window.open(
            `./alipay-payment.html#${encodeURIComponent(
              current.invocationPayload
            )}`,
            "_blank",
            "noopener,noreferrer"
          );
        }
      } catch (error) {
        if (context === this.contextKey && !this.disposed) {
          this.switchOutcomeUnknown = true;
          this.verificationError = getResultMessage(
            error,
            "支付结果待核实，请刷新原预约"
          );
        }
      } finally {
        if (context === this.contextKey && !this.disposed) this.paying = false;
      }
    },
    verifyPayment() {
      const context = this.contextKey;
      const current = this.activePaymentRequest;
      if (
        !this.showVerifyButton ||
        !current ||
        this.verifying ||
        this.refreshing ||
        this.cancellationLoading ||
        this.switchingPayment ||
        this.paying
      )
        return Promise.resolve();
      this.verifying = true;
      this.verificationError = "";
      this.verificationMessage = "";
      return this.verifyPhotographyAppointmentPayment({
        appointmentNo: this.appointmentNo,
        paymentRequestNo: current.paymentRequestNo,
      })
        .then((payment) => {
          if (context !== this.contextKey || this.disposed) return null;
          this.verificationMessage =
            payment.paymentStatus === PAYMENT.PENDING
              ? "尚未查到付款成功，请继续支付或稍后重试。"
              : "渠道已反馈支付结果，正在读取预约状态。";
          return payment;
        })
        .catch((error) => {
          if (context === this.contextKey && !this.disposed)
            this.verificationError = getResultMessage(
              error,
              "支付结果暂时无法确认，请稍后重试"
            );
          return null;
        })
        .finally(() => {
          if (context === this.contextKey && !this.disposed)
            this.verifying = false;
        })
        .then((payment) => (payment ? this.refreshPayment() : payment));
    },
  },
};
</script>

<style scoped>
.payment-page{max-width:1180px;margin:0 auto;padding:22px 28px 76px;color:#161823}.breadcrumb{margin-bottom:18px;color:#969bad;font-size:14px}.breadcrumb a{color:#20222b;font-weight:650}.breadcrumb span{padding:0 9px;color:#c5c8d2}.topline{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:24px}.title h1{margin:0;font-size:28px}.title p{margin:7px 0 0;color:#74788a;font-size:14px}.steps{min-width:470px;max-width:600px;margin:0;padding:0;list-style:none}.steps,.steps li{display:flex;flex:1}.steps li{align-items:center;color:#a4a9b8}.steps li:not(:last-child):after{content:"";height:1px;flex:1;margin:0 10px;background:#dfe2eb}.steps i{width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;margin-right:7px;border:1px solid #d6d9e4;border-radius:50%;font-style:normal}.steps .done i{color:#fff;background:#5c63d8;border-color:#5c63d8}.steps .current{color:#161823;font-weight:650}.steps .current i{color:#5c63d8;border-color:#5c63d8;box-shadow:0 0 0 4px #f3f4ff;background:#fff}.layout{display:grid;grid-template-columns:minmax(0,1fr) 360px;gap:22px;align-items:start}.stack{display:grid;gap:18px}.card,.state-card{background:#fff;border:1px solid #e8eaf2;border-radius:16px;box-shadow:0 7px 26px rgba(34,42,79,.04)}.state-card{min-height:320px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px;text-align:center}.state-card>i{color:#5c63d8;font-size:34px}.state-card.error>i{color:#c05d55}.state-card h2{margin:14px 0 8px}.state-card p{margin-bottom:18px;color:#74788a}.card-head{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:19px 22px;border-bottom:1px solid #f0f1f5}.card-head h2{margin:0;font-size:17px}.card-head>span{color:#969cac;font-size:12px}.edit-link{color:#5c63d8;font-size:13px}.contact-note{display:flex;align-items:flex-start;gap:12px;padding:20px 22px}.contact-note>i{color:#5c63d8;font-size:22px}.contact-note b{display:block;margin-bottom:5px}.contact-note p{color:#8c92a3;font-size:12px;line-height:1.6}.booking-body{padding:22px}.booking-type{display:inline-block;padding:5px 9px;color:#5c63d8;background:#f2f3ff;border-radius:6px;font-size:11px;font-weight:700}.booking-body>h3{margin:12px 0 16px;font-size:24px}.booking-meta{display:flex;flex-wrap:wrap;gap:10px 26px}.booking-meta>div{display:flex;gap:8px;font-size:13px}.booking-meta span{color:#959aaa}.booking-overview{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:22px 0}.overview-card{padding:15px;background:#fcfcfe;border:1px solid #eceef5;border-radius:13px}.overview-card.highlight{background:#fff8ee;border-color:#f4dfb6}.overview-card small,.overview-card span{display:block;color:#939aab;font-size:12px}.overview-card b{display:block;margin:7px 0;font-size:15px}.booking-body h4{margin:0 0 10px;font-size:13px}.booking-list{border:1px solid #eceef5;border-radius:13px;overflow:hidden}.booking-list>div{display:grid;grid-template-columns:150px 1fr;border-bottom:1px solid #f0f2f7}.booking-list>div:last-child{border-bottom:0}.booking-list dt{padding:15px;background:#fafbfe;color:#788095;font-size:13px}.booking-list dd{padding:14px 16px}.booking-list b,.booking-list small{display:block}.booking-list small{margin-top:4px;color:#979dad;font-size:11px;line-height:1.5}.result-card{display:flex;align-items:flex-start;gap:14px;padding:20px 22px}.result-card>i{color:#5c63d8;font-size:28px}.result-card>div{flex:1}.result-card h2{margin:1px 0 6px;font-size:17px}.result-card p{color:#777e90;line-height:1.55}.result-card .updated{margin-top:5px;font-size:11px}.result-duplicate_refunded,.result-late_refunded,.result-paid{background:#edf8f3;border-color:#ccebdd}.result-duplicate_refunded>i,.result-late_refunded>i,.result-paid>i{color:#2f8a67}.result-closed,.result-create_failed,.result-expired{background:#fff7f4;border-color:#f0d6cf}.result-closed>i,.result-create_failed>i,.result-expired>i{color:#c05d55}.side-card{position:sticky;top:18px;overflow:hidden}.order-id{padding:18px 20px 13px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #f0f1f5;color:#9297a8;font-size:12px}.order-id strong{max-width:210px;overflow-wrap:anywhere;color:#4e5364;font-weight:600}.summary{padding:18px 20px}.summary h2{margin:0 0 12px;font-size:17px}.price-row{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:7px 0;color:#555b6e;font-size:13px}.price-row b{color:#2f3340}.price-row.discount b{color:#2f8a67}.price-row.total{align-items:flex-end;margin-top:10px;padding-top:17px;border-top:1px solid #eceef4}.price-row.total>span{color:#2d303b;font-weight:650}.price-row.total div{color:#c77b12}.price-row.total strong{font-size:28px;letter-spacing:-1px}.price-row.total small{margin-right:2px}.payment-methods{padding:18px 20px;border-top:1px solid #f0f1f5}.payment-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}.payment-title span{color:#a0a5b5;font-size:11px}.pay{position:relative;display:flex;align-items:center;gap:10px;min-height:58px;padding:9px 11px;border:1px solid #5c63d8;border-radius:10px;background:#fbfbff;box-shadow:inset 0 0 0 1px #5c63d8}.pay img{width:34px;height:34px;object-fit:contain}.pay div{flex:1}.pay b,.pay small{display:block}.pay small{margin-top:2px;color:#9aa0af;font-size:11px}.pay>i{width:18px;height:18px;display:flex;align-items:center;justify-content:center;color:#fff;background:#5c63d8;border-radius:50%;font-size:11px}.no-channel{padding:12px;color:#9b4542;background:#fff2f1;border-radius:9px;font-size:12px;line-height:1.5}.checkout{padding:10px 20px 20px}.deadline{margin:0 0 12px;padding:9px 11px;color:#8b671f;background:#fff8ec;border-radius:9px;font-size:12px;line-height:1.5}.checkout /deep/ .el-button{width:100%;height:50px;border-radius:10px}.checkout /deep/ .el-button--warning{background:#eda633;border-color:#eda633}.agreement{margin-top:10px;color:#a2a7b5;text-align:center;font-size:11px;line-height:1.6}.why{padding:14px 20px 16px;color:#8b90a1;background:#fcfcfe;border-top:1px solid #f1f2f6;font-size:12px;line-height:1.6}.why strong{color:#5f6577}@media (max-width:900px){.payment-page{padding:20px 18px 80px}.topline{align-items:flex-start;flex-direction:column}.steps{min-width:0;width:100%;max-width:none}.layout{grid-template-columns:1fr}.side-card{position:static;grid-row:1}}@media (max-width:560px){.payment-page{padding:16px 12px 72px}.title h1{font-size:23px}.steps span{display:none}.steps li{justify-content:center}.steps li:not(:last-child):after{margin:0 7px}.card{border-radius:13px}.booking-list>div,.booking-overview{grid-template-columns:1fr}.booking-list dt{padding:12px 15px 6px;background:#fff}.booking-list dd{padding:0 15px 13px}.booking-body,.card-head{padding-right:16px;padding-left:16px}.result-card{flex-wrap:wrap}.result-card .el-button{width:100%}}@media (prefers-reduced-motion:reduce){*{transition:none!important}}.service-detail-title{margin-top:22px!important}.creator-service-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));border:1px solid #eceef5;border-radius:13px;overflow:hidden}.creator-service{min-width:0;padding:15px 16px}.creator-service:first-child{border-right:1px solid #f0f2f7}.creator-service>span{display:block;margin-bottom:7px;color:#788095;font-size:13px}.creator-service>b{display:block}.creator-profile{display:flex;align-items:center;gap:12px;padding:10px 12px;border:1px solid #d8e2ff;border-radius:10px;background:#f6f8ff}.makeup-service .creator-profile{border-color:#f3d7e2;background:#fff7fa}.creator-profile img{width:58px;height:66px;flex:0 0 auto;object-fit:cover;border:2px solid #dbe5ff;border-radius:8px}.makeup-service .creator-profile img{border-color:#f6dbe5}.creator-profile b,.creator-profile em,.creator-profile small{display:block}.creator-profile small{color:#5876bc;font-size:11px}.makeup-service .creator-profile em,.makeup-service .creator-profile small{color:#cf557f}.creator-profile b{margin:3px 0;color:#2f3340}.creator-profile em{color:#5876bc;font-size:11px;font-style:normal}.checkout-actions{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:9px}.checkout-actions /deep/ .el-button{width:auto}.cancellation-error{margin:10px 0 0;color:#b34c48;font-size:12px;line-height:1.5}.contact-phone{display:block;margin:2px 0 5px;color:#303442;font-size:20px;letter-spacing:.04em}.payment-switch{display:grid;gap:7px;margin-top:13px}.payment-switch /deep/ .el-button{width:100%;margin:0;border-color:#5c63d8;color:#5159c8}.payment-switch small{color:#8a90a1;font-size:11px;line-height:1.5}.payment-switch-error{margin:10px 0 0;padding:9px 11px;color:#9b4542;background:#fff2f1;border-radius:9px;font-size:12px;line-height:1.5}.native-payment{display:grid;justify-items:center;gap:7px;margin-top:14px;padding:14px;color:#353b48;background:#f4fbf7;border:1px solid #d8eee1;border-radius:10px;text-align:center}.native-payment b{font-size:13px}.native-payment small{color:#778379;font-size:11px;line-height:1.55}.verify-button{margin:10px 0 0!important}.verification-message{margin:10px 0 0;color:#5159c8;font-size:12px;line-height:1.5}@media (max-width:560px){.creator-service-grid{grid-template-columns:1fr}.creator-service:first-child{border-right:0;border-bottom:1px solid #f0f2f7}.checkout-actions{grid-template-columns:1fr}.checkout-actions /deep/ .el-button{width:100%}}
</style>

<style>
.checkout-coupon-dialog{max-width:calc(100vw - 24px);border-radius:16px;margin-top:6vh!important}.checkout-coupon-dialog .el-dialog__body{padding:0 24px;max-height:65vh;overflow:auto}.checkout-coupon-dialog .el-dialog__footer{padding:16px 24px}.checkout-coupon-dialog .el-button--primary{background:#6862d5;border-color:#6862d5}.checkout-coupon-dialog button:focus-visible{outline:3px solid #5049c4;outline-offset:3px}
</style>
<style scoped>
.summary h2{position:relative;min-height:44px;font-size:22px}.payment-orbit{position:absolute;right:0;top:-8px;width:82px;height:66px}.orbit-dot{position:absolute;right:9px;top:17px}.coupon-summary{padding:16px 20px;border-top:1px solid #e9e8f1}.coupon-summary>p{margin:0 0 8px;color:#292938}.coupon-summary>small,.coupon-summary summary{font-size:12px;line-height:1.6;color:#656576}.coupon-summary details{margin-top:10px}.coupon-summary summary{cursor:pointer}.coupon-trigger{width:100%;display:flex;justify-content:space-between;align-items:center;gap:12px;min-height:56px;border:0;background:#fff;color:#292938;cursor:pointer;text-align:left}.coupon-trigger>span{display:flex;align-items:center;gap:8px}.coupon-trigger img{width:16px;height:16px;padding:6px;background:#fff5df;border-radius:4px;box-sizing:content-box}.coupon-trigger b{color:#656576;font-size:12px;font-weight:400;overflow-wrap:anywhere}.coupon-error,.coupon-summary p{color:#ad3d35;line-height:1.6}.method-options{display:grid;gap:4px}.method-option{display:flex;align-items:center;gap:10px;width:100%;min-height:56px;padding:8px;border:0;background:#fff;color:#292938;text-align:left;cursor:pointer}.method-option.selected{background:#f5f4ff}.method-option .method-logo{width:28px;height:28px;object-fit:contain}.method-option .wallet-logo{width:22px;height:22px;margin:3px}.method-option>span:first-of-type{flex:1}.method-option b{font-weight:400;font-size:14px}.method-option small{display:block;color:#656576;font-size:12px;margin-top:4px;line-height:1.5}.method-option:disabled{cursor:not-allowed;color:#656576;background:#fafafa}.method-dot{width:18px;height:18px;flex:0 0 18px;border:1px solid #dedcf4;border-radius:50%;display:grid;place-items:center}.method-option.selected .method-dot{color:#fff;background:#7c6ff0;border-color:#7c6ff0}.checkout /deep/ .el-button--warning{background:#6862d5;border-color:#6862d5}.checkout /deep/ .el-button--warning.is-disabled{background:#a5a1e4;border-color:#a5a1e4}.price-row.total div{color:#292938}.price-row.discount b{color:#e94442}.coupon-picker{color:#292938}.coupon-picker>p{font-size:13px;line-height:1.6;color:#656576}.coupon-picker h3{font-size:14px;margin:20px 0 12px}.candidate{max-width:343px;margin:12px auto;background:#fff url("../assets/payment-checkout/d4a5b.svg") center -9px/calc(100% + 24px) auto no-repeat;box-shadow:0 3px 16px rgba(41,41,56,.03137254901960784);border-radius:8px;overflow:hidden}.candidate.unavailable{background-color:#fafafa}.candidate-select{display:flex;align-items:center;gap:12px;position:relative;width:100%;min-height:112px;border:0;padding:14px 12px;background:transparent;color:#292938;text-align:left;cursor:pointer}.candidate-select:disabled{cursor:not-allowed;color:#656576}.candidate-select>strong{width:84px;flex:0 0 84px;font-size:28px;color:#7c6ff0;overflow-wrap:anywhere;text-align:center;border-right:1px dashed #e9e8f1;padding-right:10px}.candidate-select>span{flex:1;min-width:0}.candidate-select b{display:block;font-size:17px;line-height:1.5}.candidate-select small{display:block;font-size:12px;color:#656576;line-height:1.65;margin-top:3px}.candidate-select i{font-style:normal;color:#7c6ff0;font-size:22px}.rule-toggle{margin:0 12px 8px 110px;min-height:32px;background:none;color:#5c53ca;border:0;cursor:pointer;font-size:12px}.candidate /deep/ .coupon-rules{padding:12px}.none-coupon{display:flex;justify-content:space-between;align-items:center;background:#fff;width:100%;min-height:52px;border:0;color:#656576;cursor:pointer}.selection-icon{width:20px;height:20px;flex:0 0 20px}.coupon-preview{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;align-items:center;border-top:1px solid #eeedf6;padding:14px 0;color:#656576;font-size:13px}.coupon-preview b{color:#292938;font-size:19px}.payment-page button:focus-visible{outline:3px solid #5049c4;outline-offset:3px}.checkout-actions /deep/ .el-button+.el-button{margin-left:0}.payment-title span{display:none}@media (max-width:560px){.coupon-picker .candidate-select>strong{width:64px;flex-basis:64px;font-size:24px}.rule-toggle{margin-left:90px}.coupon-trigger b{max-width:65%}.checkout-actions{grid-template-columns:1fr auto}.candidate-select{gap:10px}.summary h2{font-size:22px}.coupon-preview{font-size:12px}}
</style>
